import { configureApi } from '@api/api'
import { queryClient } from '@api/query-client'
import { consumeAuthFromHash } from '@core/utils/auth-from-hash'
import { activateChunkRecovery } from '@core/utils/error-handling/activate-chunk-recovery'
import { rootErrorHandlers } from '@core/utils/error-handling/root-error-handlers'
import { ThemeProvider } from '@design/contexts/ThemeContext'
import { TooltipProvider } from '@design/ui/tooltip'
import i18n, { i18nInit } from '@discover/i18n/i18n'
import { router } from '@discover/router'
import { initSentry } from '@discover/sentry'
import { ROUTES } from '@discover/utils/router/paths'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { I18nextProvider } from 'react-i18next'

import '@discover/index.css'

if (import.meta.env.VITE_DISCOVER_SENTRY_DSN) {
  initSentry(router)
}

activateChunkRecovery(router)

// Consume auth token from URL hash fragment (cross-app handoff from Platform)
consumeAuthFromHash()

i18nInit.then(() => {
  // Configure the shared API instance with discover-specific settings
  configureApi({
    onUnauthorized: () => router.navigate({ to: ROUTES.logOut }),
    language: i18n.language,
  })

  const rootElement = document.getElementById('root')
  if (!rootElement) throw new Error('Root element #root not found')

  const root = ReactDOM.createRoot(rootElement, rootErrorHandlers)
  root.render(
    <StrictMode>
      {/* Context Providers */}
      <I18nextProvider i18n={i18n}>
        <TooltipProvider>
          <ThemeProvider defaultTheme='system' storageKey='discover-ui-theme'>
            <QueryClientProvider client={queryClient}>
              {/* Main content */}
              <RouterProvider router={router} />
            </QueryClientProvider>
          </ThemeProvider>
        </TooltipProvider>
      </I18nextProvider>
    </StrictMode>
  )
})
